import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../hooks';
import { loadUser } from '../auth/authSlice';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { User } from '../auth/authSlice';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import TicketSummaryTable from './payment/TicketSummaryTable';
import TicketCardEditor from './payment/TicketCardEditor';
import TicketLinksPanel from './payment/TicketLinksPanel';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

const PaymentForm: React.FC = () => {
  const t = useBrandRegistrationCopy();
  const { control, watch, formState: { errors }, setValue } = useFormContext();
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const user = auth.user as User | null;

  const [mainTab, setMainTab] = useState<'tickets' | 'links'>('tickets');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!user && auth.isAuthenticated) {
      dispatch(loadUser());
    }
  }, [user, auth.isAuthenticated, dispatch]);

  const parishFeePercentage = user?.parish?.feePercentage ?? 6.9;
  const slug = watch('slug') || '';
  const tickets = watch('tickets') || [];

  const { fields, append, update } = useFieldArray({
    control,
    name: 'tickets',
  });

  const isTicketPaid = (index: number) => {
    const ticketPrice = watch(`tickets.${index}.price`);
    return ticketPrice > 0;
  };

  const getFieldError = (index: number, field: string) => {
    if (!errors.tickets) return undefined;
    const ticketErrors = errors.tickets as Record<number, Record<string, { message?: string }>>;
    return ticketErrors[index]?.[field];
  };

  const handleAddTicket = () => {
    const nextIndex = fields.length;
    append({
      name: `Ingresso ${fields.length + 1}`,
      description: '',
      price: 0,
      quantity: 50,
      startSaleDate: new Date(),
      endSaleDate: new Date(new Date().setDate(new Date().getDate() + 30)),
      isRestricted: false,
      allowedEmails: [],
      minPerOrder: 1,
      maxPerOrder: null,
      allowSameAttendeeData: true,
      isActive: true,
      hiddenFromPublicPage: false,
    });
    setExpandedIndex(nextIndex);
    setMainTab('tickets');
  };

  const handleDeactivateTicket = (index: number) => {
    const currentTicket = watch(`tickets.${index}`);
    update(index, { ...currentTicket, isActive: false });
    if (expandedIndex === index) setExpandedIndex(null);
  };

  const handleReactivateTicket = (index: number) => {
    const currentTicket = watch(`tickets.${index}`);
    update(index, { ...currentTicket, isActive: true });
    setExpandedIndex(index);
  };

  const handleTicketTypeChange = (index: number, isPaid: boolean) => {
    if (!isPaid) {
      setValue(`tickets.${index}.price`, 0);
      return;
    }
    const currentPrice = watch(`tickets.${index}.price`);
    if (currentPrice === 0 || !currentPrice) {
      setValue(`tickets.${index}.price`, 2);
    }
  };

  const handleAddEmailToList = (index: number, emailInput: string) => {
    if (!emailInput?.trim()) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = emailInput
      .split(/[,\n;]/)
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email && emailRegex.test(email));

    if (emails.length === 0) return;

    const currentEmails = watch(`tickets.${index}.allowedEmails`) || [];
    setValue(`tickets.${index}.allowedEmails`, Array.from(new Set([...currentEmails, ...emails])));
  };

  const handleRemoveEmailFromList = (index: number, emailToRemove: string) => {
    const currentEmails = watch(`tickets.${index}.allowedEmails`) || [];
    setValue(
      `tickets.${index}.allowedEmails`,
      currentEmails.filter((email: string) => email !== emailToRemove)
    );
  };

  const handleToggleRestricted = (index: number, isRestricted: boolean) => {
    setValue(`tickets.${index}.isRestricted`, isRestricted);
    if (!isRestricted) {
      setValue(`tickets.${index}.allowedEmails`, []);
      return;
    }
    const currentEmails = watch(`tickets.${index}.allowedEmails`);
    if (!currentEmails) setValue(`tickets.${index}.allowedEmails`, []);
  };

  const handleImportEmailsFromFile = (index: number, file: File) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        if (!text) return;

        const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
        const foundEmails = text.match(emailRegex) || [];
        const normalizedEmails = foundEmails.map((email) => email.toLowerCase().trim());
        const uniqueEmails = Array.from(new Set(normalizedEmails));

        if (uniqueEmails.length === 0) {
          alert('Nenhum email válido encontrado no arquivo.');
          return;
        }

        const currentEmails = watch(`tickets.${index}.allowedEmails`) || [];
        setValue(`tickets.${index}.allowedEmails`, Array.from(new Set([...currentEmails, ...uniqueEmails])));
        alert(`${uniqueEmails.length} email(s) importado(s) com sucesso!`);
      } catch (error) {
        console.error('Erro ao processar arquivo:', error);
        alert('Erro ao processar arquivo. Verifique se o formato está correto.');
      }
    };

    reader.onerror = () => alert('Erro ao ler o arquivo.');
    reader.readAsText(file);
  };

  const toggleExpanded = (index: number) => {
    setExpandedIndex((current) => (current === index ? null : index));
    setMainTab('tickets');
  };

  const totalActiveTicketCount = tickets.filter(
    (t: { isActive?: boolean }) => t.isActive !== false
  ).length;
  const activeTicketCount = tickets.filter(
    (t: { isActive?: boolean; hiddenFromPublicPage?: boolean }) =>
      t.isActive !== false && !t.hiddenFromPublicPage
  ).length;
  const hiddenTicketCount = tickets.filter(
    (t: { isActive?: boolean; hiddenFromPublicPage?: boolean }) =>
      t.isActive !== false && t.hiddenFromPublicPage
  ).length;

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        Ingressos e pagamentos
        <Tooltip title="Configure ingressos, lotes e links de divulgação">
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>

      <Paper elevation={0} variant="outlined" sx={{ mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(_, value) => setMainTab(value)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value="tickets"
            icon={<ConfirmationNumberOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Ingressos (${activeTicketCount})`}
          />
          <Tab
            value="links"
            icon={<LinkOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={`Links promocionais${hiddenTicketCount ? ` (${hiddenTicketCount})` : ''}`}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {mainTab === 'tickets' && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Visão geral dos ingressos ativos. Clique em uma linha ou no ícone de editar para abrir os detalhes.
                Para lotes sequenciais, crie ingressos com nomes como &quot;Lote 1&quot;, &quot;Lote 2&quot; e datas de venda encadeadas.
              </Typography>

              {hiddenTicketCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('Ingressos ocultos exibem o link direto na tabela e nos detalhes — compartilhe esse link para inscrições fora da página pública.')}
                </Typography>
              )}

              <TicketSummaryTable
                tickets={tickets}
                expandedIndex={expandedIndex}
                onEdit={toggleExpanded}
                slug={slug}
              />

              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, mt: 1 }}>
                Detalhes dos ingressos
              </Typography>

              {fields.map((field, index) => (
                <TicketCardEditor
                  key={field.id}
                  index={index}
                  expanded={expandedIndex === index}
                  onToggle={() => toggleExpanded(index)}
                  slug={slug}
                  parishFeePercentage={parishFeePercentage}
                  canDeactivate={totalActiveTicketCount > 1}
                  onDeactivate={() => handleDeactivateTicket(index)}
                  onReactivate={() => handleReactivateTicket(index)}
                  getFieldError={getFieldError}
                  isTicketPaid={isTicketPaid}
                  onTicketTypeChange={handleTicketTypeChange}
                  onAddEmail={handleAddEmailToList}
                  onRemoveEmail={handleRemoveEmailFromList}
                  onToggleRestricted={handleToggleRestricted}
                  onImportEmails={handleImportEmailsFromFile}
                />
              ))}

              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddTicket}
                sx={{ mt: 1 }}
              >
                Adicionar tipo de ingresso
              </Button>
            </>
          )}

          {mainTab === 'links' && <TicketLinksPanel slug={slug} tickets={tickets} />}
        </Box>
      </Paper>

      {fields.some((_, index) => isTicketPaid(index)) && (
        <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            Configurações de pagamento
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 0 }}>
            {t(`Taxa da plataforma: ${parishFeePercentage.toFixed(1).replace('.', ',')}% por inscrição paga.`)}
            Em cada ingresso pago você pode escolher se a taxa aparece separada para o participante.
          </Typography>
        </Paper>
      )}

      <Typography variant="body2" color="text.secondary">
        Dica: use a aba &quot;Links promocionais&quot; para criar desconto em % — o sistema calcula o preço e gera o link.
      </Typography>
    </Box>
  );
};

export default PaymentForm;
